import React from 'react';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { CreditCard, Check, X } from 'lucide-react';
import { useCardPayment } from '@/hooks/useCardPayment';

interface CardPaymentButtonProps {
  amount: number;
  onPaymentSuccess: () => void;
  disabled?: boolean;
}

export const CardPaymentButton: React.FC<CardPaymentButtonProps> = ({ 
  amount, 
  onPaymentSuccess, 
  disabled 
}) => {
  const { status, processCardPayment, isProcessing } = useCardPayment();

  const handleCardPayment = async () => {
    if (amount <= 0) return;
    
    const result = await processCardPayment(amount);
    
    if (result.success) {
      // Attendre un peu pour que l'utilisateur voit le succès, puis procéder
      setTimeout(onPaymentSuccess, 1500);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const getButtonContent = () => {
    switch (status) {
      case 'processing':
        return (
          <>
            <LoadingSpinner variant="minimal" size="sm" />
            En attente de paiement...
          </>
        );
      case 'success':
        return (
          <>
            <Check className="w-4 h-4 mr-2" />
            Paiement accepté ✅
          </>
        );
      case 'error':
        return (
          <>
            <X className="w-4 h-4 mr-2" />
            Paiement refusé ❌
          </>
        );
      default:
        return (
          <>
            <CreditCard className="w-5 h-5 mr-2" />
            Encaisser par CB 💳
            {amount > 0 && (
              <span className="ml-2 font-semibold">
                {formatPrice(amount)}
              </span>
            )}
          </>
        );
    }
  };

  const getButtonVariant = () => {
    switch (status) {
      case 'success':
        return 'default';
      case 'error':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  return (
    <Button
      onClick={handleCardPayment}
      disabled={disabled || amount <= 0 || isProcessing}
      variant={getButtonVariant()}
      className={`
        transition-all duration-300 min-w-[200px] justify-start
        ${status === 'success' ? 'bg-green-600 hover:bg-green-700 text-white border-green-600' : ''}
        ${status === 'processing' ? 'animate-pulse' : ''}
        ${status === 'idle' ? 'hover:bg-primary/10 hover:border-primary/40 hover:text-primary' : ''}
      `}
      size="lg"
    >
      {getButtonContent()}
    </Button>
  );
};